// DocumentoLegalSection.js — Documento legal (términos de uso, privacidad, aviso
// legal) con el lenguaje del catálogo streaming: encabezado con la fecha de
// actualización, índice lateral fijo que marca el apartado que se está leyendo
// y apartados numerados a un ancho cómodo de lectura. Tipo `documento_legal`.
//
// Texto plano: una línea en blanco separa párrafos; «### » al inicio de un
// bloque es un subtítulo; las líneas que empiezan con «- » forman una lista; los
// correos y enlaces se vuelven clicables.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import MuroCursos from './MuroCursos';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";

const Raiz = styled.div`
  --acento: ${p => p.$acento};
  background: ${p => p.$fondo}; color: #d5d9df; font-family: ${FUENTE};
`;
const Cabecera = styled.header`
  position: relative; overflow: hidden; isolation: isolate;
  min-height: ${p => (p.$banner ? 'clamp(460px, 62vh, 620px)' : 'auto')}; display: flex; flex-direction: column; justify-content: flex-end;
  padding: clamp(8rem, 14vw, 10rem) 4vw clamp(2.5rem, 5vw, 3.5rem);
  border-bottom: 1px solid rgba(255,255,255,.08);
  .eyebrow { position: relative; display: inline-flex; align-items: center; gap: .6rem; font-size: .92rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { position: relative; margin: 1rem 0 0; font-size: clamp(2.6rem, 6vw, 4.8rem); font-weight: 800; font-stretch: 78%; line-height: .95; letter-spacing: -.015em; color: #fff; }
  .meta { position: relative; display: flex; flex-wrap: wrap; gap: .6rem; margin-top: 1.4rem; }
  .meta span { padding: .35rem .8rem; border-radius: 999px; font-size: .82rem; color: #c6ccd4; background: rgba(255,255,255,.06); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
  .intro { position: relative; max-width: 46rem; margin: 1.4rem 0 0; font-size: 1.08rem; line-height: 1.65; color: #b9c0ca; }
`;
const Cuerpo = styled.div`
  display: grid; grid-template-columns: 260px minmax(0, 1fr); gap: 4rem; padding: clamp(2.5rem, 5vw, 4rem) 4vw clamp(4rem, 7vw, 6rem);
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 2rem; }
`;
const Indice = styled.nav`
  position: sticky; top: 6rem; align-self: start;
  h2 { margin: 0 0 .9rem; font-size: .85rem; font-weight: 700; color: #8a929d; }
  ol { list-style: none; margin: 0; padding: 0; border-left: 1px solid rgba(255,255,255,.1); }
  a {
    position: relative; display: flex; gap: .6rem; padding: .45rem 0 .45rem 1rem; margin-left: -1px; border-left: 2px solid transparent;
    font-size: .88rem; line-height: 1.35; color: #8a929d; text-decoration: none; transition: color .2s, border-color .2s;
  }
  a b { font-weight: 700; font-variant-numeric: tabular-nums; color: inherit; min-width: 1.3em; }
  a:hover { color: #fff; }
  a[aria-current='true'] { color: #fff; border-left-color: var(--acento); }
  a[aria-current='true'] b { color: var(--acento); }
  a:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  @media (max-width: 900px) { position: static; ol { columns: 2; column-gap: 1.5rem; } }
  @media (max-width: 560px) { ol { columns: 1; } }
`;
const Texto = styled.div`
  max-width: 44rem;
  section { padding-bottom: 2.4rem; margin-bottom: 2.4rem; border-bottom: 1px solid rgba(255,255,255,.07); scroll-margin-top: 6rem; }
  section:last-of-type { border-bottom: 0; }
  h2 { display: flex; align-items: baseline; gap: .9rem; margin: 0 0 1rem; font-size: clamp(1.3rem, 2.2vw, 1.7rem); font-weight: 800; font-stretch: 85%; color: #fff; line-height: 1.15; }
  h2 span { font-size: .95em; color: var(--acento); font-variant-numeric: tabular-nums; }
  p { margin: 0 0 1rem; font-size: 1rem; line-height: 1.75; color: #c3c9d1; }
  h3 { margin: 1.8rem 0 .6rem; font-size: 1.05rem; font-weight: 700; color: #fff; }
  h3:first-of-type { margin-top: .4rem; }
  ul { margin: 0 0 1rem; padding: 0; list-style: none; }
  li { position: relative; padding-left: 1.3rem; margin-bottom: .55rem; line-height: 1.65; color: #c3c9d1; }
  li::before { content: ''; position: absolute; left: 0; top: .72em; width: 6px; height: 6px; border-radius: 50%; background: var(--acento); }
  a { color: #fff; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 3px; }
`;
const Contacto = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem 2rem; flex-wrap: wrap;
  padding: 1.5rem 1.7rem; border-radius: 18px; margin-top: .5rem;
  background: linear-gradient(120deg, color-mix(in srgb, var(--acento) 16%, transparent), rgba(255,255,255,.03) 65%);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
  strong { display: block; font-size: 1.1rem; color: #fff; }
  span { color: #b9c0ca; font-size: .92rem; }
  a { display: inline-flex; align-items: center; height: 44px; padding: 0 1.2rem; border-radius: 999px; text-decoration: none;
    background: var(--acento); color: #fff; font-weight: 700; }
  a:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
`;

const ENLACE = /((?:https?:\/\/|www\.)[^\s]+[^\s.,;:)]|[\w.+-]+@[\w-]+\.[\w.-]+\w)/g;
const conEnlaces = (t, k) => t.split(ENLACE).map((x, i) => (i % 2
  ? <a key={`${k}-${i}`} href={x.includes('@') && !x.startsWith('http') ? `mailto:${x}` : (x.startsWith('www.') ? `https://${x}` : x)} target={x.includes('@') ? undefined : '_blank'} rel="noopener noreferrer">{x}</a>
  : x));

// Párrafos y listas («- ») desde texto plano.
const Parrafos = ({ texto }) => String(texto || '').replace(/\r\n?/g, '\n').split(/\n\s*\n/).map(b => b.trim()).filter(Boolean).map((b, i) => {
  const lineas = b.split('\n');
  if (/^###\s+/.test(b)) return <h3 key={i}>{b.replace(/^###\s+/, '')}</h3>;
  if (lineas.every(l => /^\s*-\s+/.test(l))) {
    return <ul key={i}>{lineas.map((l, j) => <li key={j}>{conEnlaces(l.replace(/^\s*-\s+/, ''), `${i}-${j}`)}</li>)}</ul>;
  }
  return <p key={i}>{lineas.map((l, j) => <React.Fragment key={j}>{conEnlaces(l, `${i}-${j}`)}{j < lineas.length - 1 && <br />}</React.Fragment>)}</p>;
});

const slug = (s, i) => `apartado-${i + 1}-${String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)}`;

const DocumentoLegalSection = ({ config = {}, proyecto }) => {
  const c = config;
  const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = c.color_acento || cat.color_acento || '#E50914';
  const fondo = c.color_fondo || cat.color_fondo || '#141414';
  const apartados = useMemo(() => (Array.isArray(c.apartados) ? c.apartados : []).filter(a => a.titulo)
    .map((a, i) => ({ ...a, id: slug(a.titulo, i) })), [c.apartados]);
  const [activo, setActivo] = useState(apartados[0]?.id);

  // El índice marca el apartado que está en la parte alta de la pantalla.
  useEffect(() => {
    if (!apartados.length || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entradas) => {
      const visibles = entradas.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visibles[0]) setActivo(visibles[0].target.id);
    }, { rootMargin: '-20% 0px -65% 0px' });
    apartados.forEach(a => { const el = document.getElementById(a.id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, [apartados]);

  const irA = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    setActivo(id);
  };

  return (
    <Raiz $acento={acento} $fondo={fondo}>
      <Cabecera $banner={c.mostrar_banner !== false}>
        {c.mostrar_banner !== false && <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} imagen={c.imagen_banner} opacidad={0.7} />}
        {c.eyebrow && <span className="eyebrow">{c.eyebrow}</span>}
        <h1>{c.titulo || 'Documento'}</h1>
        {(c.actualizado || c.vigencia) && (
          <div className="meta">
            {c.actualizado && <span>Última actualización: {c.actualizado}</span>}
            {c.vigencia && <span>{c.vigencia}</span>}
          </div>
        )}
        {c.intro && <p className="intro">{c.intro}</p>}
      </Cabecera>
      <Cuerpo>
        {apartados.length > 1 && (
          <Indice aria-label="Contenido del documento">
            <h2>Contenido</h2>
            <ol>
              {apartados.map((a, i) => (
                <li key={a.id}><a href={`#${a.id}`} onClick={e => irA(e, a.id)} aria-current={activo === a.id ? 'true' : undefined}><b>{i + 1}</b>{a.titulo}</a></li>
              ))}
            </ol>
          </Indice>
        )}
        <Texto>
          {apartados.map((a, i) => (
            <section key={a.id} id={a.id} aria-labelledby={`${a.id}-t`}>
              <h2 id={`${a.id}-t`}><span aria-hidden="true">{i + 1}.</span>{a.titulo}</h2>
              <Parrafos texto={a.texto} />
            </section>
          ))}
          {(c.contacto_titulo || c.contacto_correo) && (
            <Contacto>
              <div><strong>{c.contacto_titulo || '¿Tienes preguntas sobre este documento?'}</strong>{c.contacto_texto && <span>{c.contacto_texto}</span>}</div>
              {c.contacto_correo && <a href={/^https?:|^\//.test(c.contacto_correo) ? c.contacto_correo : `mailto:${c.contacto_correo}`}>{c.contacto_boton || 'Escríbenos'}</a>}
            </Contacto>
          )}
        </Texto>
      </Cuerpo>
    </Raiz>
  );
};

export default DocumentoLegalSection;
